import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Row } from 'react-bootstrap';
import InputNumber from 'rc-input-number';
import 'rc-input-number/assets/index.css';
import styles from './RetailerPermissions.module.scss';
import Toolbar from '../../common/Toolbar';
import SlideCard from '../../common/SlideCard';
import Button from '../../common/Button';
import RadioGroup from '../../common/RadioGroup';
import Radio from '../../common/Radio';
import { decimalFormatter, numberFormatter } from '../../../utils/formatHelper';
import { CarrierDisplayName } from '../../../constants/carrierTypes';
import { InvoiceField } from '../InvoiceField';
import { RetailerCommission } from '../RetailerCommission/RetailerCommission';
import { clamp, isEqual, merge } from 'lodash';
import {
  BILLING_METHOD_INSTANT,
  BILLING_METHOD_NONE,
  DISABLED,
  ECOMMERCE_ORDER_DEFAULT_DAYS_UNTIL_OVERDUE,
  ENABLED,
  HANDLING_FEE_LINE,
  HANDLING_FEE_ORDER,
  REPLENISH_ORDER_DEFAULT_DAYS_UNTIL_OVERDUE,
  SHIPPING_CALC_TYPE_FIXED_PER_UNIT,
  SHIPPING_CALC_TYPE_PERCENTAGE,
} from '../../../constants/permissions';
import { useSelector } from 'react-redux';
import InfoIcon from '../../common/AlertTemplate/icons/InfoIcon';
import RequestSupplierReorderModal from '../../modals/RequestSupplierReorderModal';

const ORDER_EXPIRATION_DAYS_MIN = 1;
const DEFAULT_ORDER_EXPIRATION_DAYS = 14;
const ORDER_EXPIRATION_DAYS_MAX = 365;

function RetailerPermissions(props) {
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [permissions, setPermissions] = useState(getInitialPermissionsFromProps(props));

  const [shippingCalcType, setShippingCalcType] = useState(
    props.replenishment_order_shipping_cost_percentage > 0
      ? SHIPPING_CALC_TYPE_PERCENTAGE
      : SHIPPING_CALC_TYPE_FIXED_PER_UNIT,
  );

  const ecommerceDaysUntilOverdue =
    props.settings?.ecommerce_days_until_overdue_email || ECOMMERCE_ORDER_DEFAULT_DAYS_UNTIL_OVERDUE;
  const replenishDaysUntilOverdue =
    props.settings?.replenish_days_until_overdue_email || REPLENISH_ORDER_DEFAULT_DAYS_UNTIL_OVERDUE;

  const minimumOrderDescription = () => {
    const minimums = [];
    const { minimum_products_for_replenishment_order, minimum_order_value_for_replenishment_order } = props;

    if (minimum_products_for_replenishment_order > 0) {
      minimums.push(`${minimum_products_for_replenishment_order} Products`);
    }

    if (minimum_order_value_for_replenishment_order > 0) {
      minimums.push(`Order value of ${minimum_order_value_for_replenishment_order}`);
    }

    return minimums.length ? minimums.join(' / ') : 'No Minimums';
  };

  const onChangeShippingCost = (value) => {
    setShippingCalcType(value);

    props.setPermissionsFixedPerUnit(0);
    props.setPermissionsPercentage(0);
  };

  const onSubmit = () => {
    const {
      id,
      ecommerce_unit_price_formula,
      approved_for_ecommerce,
      approved_for_replenishment,
      minimum_products_for_replenishment_order,
      minimum_order_value_for_replenishment_order,
      replenishment_order_shipping_cost_percentage,
      replenishment_order_shipping_cost_fixed_per_unit,
      putPermissions,
    } = props;

    const newShippingCost = [
      {
        percentage: replenishment_order_shipping_cost_percentage,
        fixed_per_unit: replenishment_order_shipping_cost_fixed_per_unit,
      },
    ];
    const newSettings = {
      ...props.settings,
      ecommerce_days_until_overdue_email: ecommerceDaysUntilOverdue,
      replenish_days_until_overdue_email: replenishDaysUntilOverdue,
      orderExpirationDays: permissions.orderExpirationDays
        ? clamp(permissions.orderExpirationDays, ORDER_EXPIRATION_DAYS_MIN, ORDER_EXPIRATION_DAYS_MAX)
        : undefined,
    };

    let handlingFeeFormula = null;
    if (permissions.handlingFee.enabled && permissions.handlingFee.formula !== null) {
      handlingFeeFormula = {
        type: permissions.handlingFee.type,
        formula: Math.round(permissions.handlingFee.formula * 100).toString(),
      };
    }

    const permissionData = {
      approved_for_ecommerce: !!approved_for_ecommerce,
      approved_for_replenishment: !!approved_for_replenishment,
      ecommerce_unit_price_formula,
      ecommerce_billing_method: permissions.ecommerceBillingMethod,
      replenish_billing_method: permissions.replenishBillingMethod,
      minimum_products_for_replenishment_order,
      minimum_order_value_for_replenishment_order,
      replenishment_order_shipping_cost: newShippingCost,
      ecommerce_send_invoice: permissions.ecommerceSendInvoice,
      ecommerce_invoice_net_plus: Number(permissions.ecommerceInvoiceNetPlus),
      replenish_send_invoice: permissions.replenishSendInvoice,
      replenish_invoice_net_plus: Number(permissions.replenishInvoiceNetPlus),
      settings: newSettings,
      refund_cost_formula: permissions.refundCost.enabled
        ? Math.round(permissions.refundCost.formula * 100).toString()
        : null,
      handling_fee_formula: handlingFeeFormula,
    };

    putPermissions(id, permissionData);
  };

  const shippingCostMessage = (shippingType) => {
    const {
      replenishment_shipper_account,
      replenishment_shipper_account_type,
      replenishment_order_shipping_cost_percentage,
      replenishment_order_shipping_cost_fixed_per_unit,
    } = props;

    if (replenishment_shipper_account) {
      const shipperAccountType = CarrierDisplayName[replenishment_shipper_account_type] || 'None';
      return `Shipper Account: ${replenishment_shipper_account} (${shipperAccountType})`;
    }

    switch (shippingType) {
      case SHIPPING_CALC_TYPE_PERCENTAGE:
        return `Percentage per item: ${replenishment_order_shipping_cost_percentage}%`;
      case SHIPPING_CALC_TYPE_FIXED_PER_UNIT:
        return `Flat rate per item: $${replenishment_order_shipping_cost_fixed_per_unit / 100}`;
      default:
        return null;
    }
  };

  const shippingCostValue = (shippingType) => {
    switch (shippingType) {
      case SHIPPING_CALC_TYPE_PERCENTAGE:
        return (
          <div className={styles.shipping_value}>
            <span className={styles.value_span}>Value:</span>
            <InputNumber
              required={true}
              min={0}
              max={100}
              data-testid="replenishment_order_shipping_cost_percentage"
              value={props.replenishment_order_shipping_cost_percentage}
              onChange={props.setPermissionsPercentage}
            />
          </div>
        );
      case SHIPPING_CALC_TYPE_FIXED_PER_UNIT:
        return (
          <div className={styles.shipping_value}>
            <span className={styles.value_span}>Value:</span>
            <InputNumber
              required={true}
              min={0}
              value={props.replenishment_order_shipping_cost_fixed_per_unit / 100}
              onChange={props.setPermissionsFixedPerUnit}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderDaysUntilOverdue = (name, value, setter) => {
    return (
      <SlideCard title="SLA for shipping (in days)" testId={name}>
        <InputNumber
          name={name}
          onChange={setter}
          min={1}
          max={100}
          onFocus={(e) => e.target.select()}
          onClick={(e) => e.target.select()}
          value={value}
          formatter={numberFormatter}
        />
      </SlideCard>
    );
  };

  const renderEcommerceDaysUntilOverdue = () => {
    return renderDaysUntilOverdue(
      'ecommerceDaysUntilOverdueEmail',
      ecommerceDaysUntilOverdue,
      props.changeEcommerceDaysUntilOverdue,
    );
  };

  const renderReplenishDaysUntilOverdue = () => {
    return renderDaysUntilOverdue(
      'replenishDaysUntilOverdueEmail',
      replenishDaysUntilOverdue,
      props.changeReplenishDaysUntilOverdue,
    );
  };

  const updatePermissions = (updatedPermissions) => {
    const newPermissions = merge({}, permissions, updatedPermissions);

    if (!isEqual(permissions, newPermissions)) {
      setPermissions((permissions) => merge({}, permissions, updatedPermissions));
      props.setPermissionsChanged();
    }
  };

  function renderDropshipTerms() {
    const { approved_for_ecommerce, changeDropshipAllowed } = props;

    const dropshipBillingMethodDescription =
      permissions.ecommerceBillingMethod === BILLING_METHOD_INSTANT ? 'Auto billing (stripe)' : 'Manual (By report)';

    return (
      <>
        <Toolbar name="Drop Ship Terms" />

        <SlideCard
          title="Drop Ship service status"
          description={approved_for_ecommerce ? 'Active' : 'Disabled'}
          testId="dropship-status"
        >
          <RadioGroup
            name="dropshipStatus"
            onChange={changeDropshipAllowed}
            selectedvalue={approved_for_ecommerce ? 'true' : 'false'}
          >
            <Radio id="dropshipStatusActive" value="true" label="Active" />
            <Radio id="dropshipStatusDisabled" value="false" label="Disabled" />
          </RadioGroup>
        </SlideCard>

        {approved_for_ecommerce && (
          <>
            <SlideCard
              title="Drop Ship billing method"
              description={dropshipBillingMethodDescription}
              testId="dropship-billing-method"
            >
              <RadioGroup
                name="billingMethod"
                selectedvalue={permissions.ecommerceBillingMethod}
                onChange={(value) => updatePermissions({ ecommerceBillingMethod: value })}
              >
                <Radio
                  id="ecommerce_billing_method_instant"
                  value={BILLING_METHOD_INSTANT}
                  label="Auto billing (stripe)"
                />
                <Radio
                  id="ecommerce_billing_method_none"
                  value={BILLING_METHOD_NONE}
                  label="Manual billing (by report)"
                />
              </RadioGroup>
            </SlideCard>

            {props.has_stripe && (
              <InvoiceField
                type="ecommerce"
                sendInvoice={props.ecommerce_send_invoice}
                invoiceNetPlus={props.ecommerce_invoice_net_plus}
                onChange={(enabled, paymentTerms) => {
                  updatePermissions({
                    ecommerceSendInvoice: enabled,
                    ecommerceInvoiceNetPlus: paymentTerms,
                  });
                }}
              />
            )}

            <RetailerCommission onChange={props.changeDropshipRevenue} formula={props.ecommerce_unit_price_formula} />
            {renderEcommerceDaysUntilOverdue()}

            <SlideCard
              title="Refund Fee"
              description="The amount to be deducted per return"
              testId="dropship-refund-fee"
            >
              <RadioGroup
                name="refundCostEnabled"
                onChange={(value) =>
                  updatePermissions({
                    refundCost: { enabled: value === ENABLED },
                  })
                }
                selectedvalue={permissions.refundCost.enabled ? ENABLED : DISABLED}
              >
                <Radio id="refund_cost_enabled" value={ENABLED} label="Enabled" />
                <Radio id="refund_cost_disabled" value={DISABLED} label="Disabled" />
              </RadioGroup>

              {permissions.refundCost.enabled && (
                <div className={styles.refundFee}>
                  <label>Refund Fee:</label>
                  <InputNumber
                    onChange={(value) => updatePermissions({ refundCost: { formula: value } })}
                    min={0}
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.target.select()}
                    value={permissions.refundCost.formula}
                    formatter={decimalFormatter}
                    step={0.1}
                  />
                </div>
              )}
            </SlideCard>

            {props.is_super_admin && (
              <SlideCard
                title="Order Expiration (in days)"
                description="The order is passed the SLA of the sales channel and will expire in Cymbio's platform."
                testId="order-expiration-days"
              >
                <InputNumber
                  name="order-expiration-days"
                  onChange={(value) => updatePermissions({ orderExpirationDays: value })}
                  min={ORDER_EXPIRATION_DAYS_MIN}
                  max={ORDER_EXPIRATION_DAYS_MAX}
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.target.select()}
                  value={permissions.orderExpirationDays}
                  formatter={numberFormatter}
                />
              </SlideCard>
            )}

            <SlideCard
              title="Handling Fee"
              description="Handling fee agreed upon and will be paid by the sales channel."
              testId="dropship-handling-fee"
            >
              <RadioGroup
                name="handlingFeeEnabled"
                onChange={(value) => updatePermissions({ handlingFee: { enabled: value === ENABLED } })}
                selectedvalue={permissions.handlingFee.enabled ? ENABLED : DISABLED}
              >
                <Radio id="handling_fee_enabled" value={ENABLED} label="Enabled" />
                <Radio id="handling_fee_disabled" value={DISABLED} label="Disabled" />
              </RadioGroup>

              {permissions.handlingFee.enabled && (
                <div className={styles.handlingFee}>
                  <label>Choose Handling Fee Type:</label>
                  <RadioGroup
                    name="handlingFeeType"
                    onChange={(value) => updatePermissions({ handlingFee: { type: value } })}
                    selectedvalue={permissions.handlingFee.type}
                  >
                    <Radio id="handlingFeeTypeOrder" value={HANDLING_FEE_ORDER} label="Order" />
                    <Radio id="handlingFeeTypeLine" value={HANDLING_FEE_LINE} label="Line" />
                  </RadioGroup>

                  <label>Handling Fee Formula:</label>
                  <InputNumber
                    onChange={(value) => updatePermissions({ handlingFee: { formula: value } })}
                    min={0}
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => e.target.select()}
                    value={permissions.handlingFee.formula}
                    formatter={decimalFormatter}
                    step={0.1}
                  />
                </div>
              )}
            </SlideCard>
          </>
        )}
      </>
    );
  }
  const { is_reorder_enabled: isReorderEnabledForSupplier, id: supplierId } = useSelector(
    (state) => state.role.supplier,
  );
  const supplierReorderRequest = useSelector((state) => state.supplierReorderRequest.reorder_email_sent_for_suppliers);
  function renderReorderTerms() {
    const {
      approved_for_replenishment,
      minimum_products_for_replenishment_order,
      minimum_order_value_for_replenishment_order,
      replenishment_shipper_account,
      changeReplenishAllowed,
      setPermissionsMinimumOrderProducts,
      setPermissionsMinimumOrderValue,
    } = props;

    const replenishBillingMethodDescription =
      permissions.replenishBillingMethod === BILLING_METHOD_INSTANT ? 'Auto billing (stripe)' : 'Manual (By report)';

    const infoButton = () => (
      <span className={styles.reorderInfoIcon} onClick={() => setIsReorderModalOpen(true)}>
        <InfoIcon />
      </span>
    );
    return (
      <>
        <RequestSupplierReorderModal
          isOpen={isReorderModalOpen}
          onClose={() => setIsReorderModalOpen(false)}
          wasEmailSent={supplierReorderRequest.find((requestSupplierId) => supplierId === requestSupplierId)}
        />
        <Toolbar name="Reorder Terms" />

        <SlideCard
          title="Reorder service status"
          description={approved_for_replenishment ? 'Active' : 'Disabled'}
          testId="reorder-status"
          actionButton={!isReorderEnabledForSupplier ? infoButton() : null}
        >
          <RadioGroup
            name="replenishStatus"
            selectedvalue={approved_for_replenishment ? 'true' : 'false'}
            onChange={changeReplenishAllowed}
          >
            <Radio disabled={!isReorderEnabledForSupplier} id="replenishStatusActive" value="true" label="Active" />
            <Radio
              disabled={!isReorderEnabledForSupplier}
              id="replenishStatusDisabled"
              value="false"
              label="Disabled"
            />
          </RadioGroup>
        </SlideCard>

        {approved_for_replenishment && (
          <>
            <SlideCard
              title="Reorder billing method"
              description={replenishBillingMethodDescription}
              testId="reorder-billing-method"
            >
              <RadioGroup
                name="replenishMethod"
                selectedvalue={permissions.replenishBillingMethod}
                onChange={(value) =>
                  updatePermissions({
                    replenishBillingMethod: value,
                  })
                }
              >
                <Radio
                  id="replenish_billing_method_instant"
                  value={BILLING_METHOD_INSTANT}
                  label="Auto billing (stripe)"
                />
                <Radio
                  id="replenish_billing_method_none"
                  value={BILLING_METHOD_NONE}
                  label="Manual billing (by report)"
                />
              </RadioGroup>
            </SlideCard>

            {props.has_stripe && (
              <InvoiceField
                type="replenish"
                sendInvoice={props.replenish_send_invoice}
                invoiceNetPlus={props.replenish_invoice_net_plus}
                onChange={(enabled, paymentTerms) => {
                  updatePermissions({
                    replenishSendInvoice: enabled,
                    replenishInvoiceNetPlus: paymentTerms,
                  });
                }}
              />
            )}

            <SlideCard title="Minimum order" description={minimumOrderDescription()} testId="reorder-minimum-order">
              <div className={styles.minimum_order}>
                <label>Products:</label>
                <InputNumber
                  min={0}
                  value={minimum_products_for_replenishment_order}
                  onChange={(value) => setPermissionsMinimumOrderProducts(parseInt(value, 10))}
                  formatter={numberFormatter}
                />
              </div>

              <div className={styles.minimum_order}>
                <label>Order Value:</label>
                <InputNumber
                  min={0}
                  value={minimum_order_value_for_replenishment_order}
                  onChange={(value) => setPermissionsMinimumOrderValue(parseInt(value, 10))}
                  formatter={numberFormatter}
                />
              </div>
            </SlideCard>
            <SlideCard
              title="Shipping cost calculation"
              description={shippingCostMessage(shippingCalcType)}
              testId="reorder-shipping-cost"
            >
              {replenishment_shipper_account ? (
                <span>
                  You can change <b>Shipper Account</b> in Settings Tab
                </span>
              ) : (
                <div className={styles.shipping}>
                  <RadioGroup name="shippingCost" selectedvalue={shippingCalcType} onChange={onChangeShippingCost}>
                    <Radio
                      id={SHIPPING_CALC_TYPE_FIXED_PER_UNIT}
                      value={SHIPPING_CALC_TYPE_FIXED_PER_UNIT}
                      label="Flat rate per item"
                    />
                    <Radio
                      id={SHIPPING_CALC_TYPE_PERCENTAGE}
                      value={SHIPPING_CALC_TYPE_PERCENTAGE}
                      label="Percentage per item"
                    />
                  </RadioGroup>

                  {shippingCostValue(shippingCalcType)}
                </div>
              )}
            </SlideCard>

            {renderReplenishDaysUntilOverdue()}
          </>
        )}
      </>
    );
  }

  return (
    <Container className={styles.container} fluid>
      <Row>
        <Col xs={12} md={6}>
          {renderDropshipTerms()}
        </Col>

        <Col xs={12} md={6}>
          {renderReorderTerms()}
        </Col>
      </Row>

      <Row className={styles.buttons}>
        <Col xs={12}>
          <Button variant="raised" text="Update Terms" onClick={onSubmit} disabled={!props.changed} />
        </Col>
      </Row>
    </Container>
  );
}

export default RetailerPermissions;

function getInitialPermissionsFromProps(props) {
  return {
    ecommerceSendInvoice: props.ecommerce_send_invoice,
    ecommerceInvoiceNetPlus: props.ecommerce_invoice_net_plus,
    ecommerceBillingMethod: props.ecommerce_billing_method,
    replenishSendInvoice: props.replenish_send_invoice,
    replenishInvoiceNetPlus: props.replenish_invoice_net_plus,
    replenishBillingMethod: props.replenish_billing_method,
    refundCost: {
      enabled: !!props.refund_cost_formula,
      formula: props.refund_cost_formula ? props.refund_cost_formula / 100 : null,
    },
    handlingFee: {
      enabled: !!props.handling_fee_formula,
      type: props.handling_fee_formula?.type || HANDLING_FEE_ORDER,
      formula: props.handling_fee_formula?.formula / 100 || null,
    },
    orderExpirationDays: props.settings?.orderExpirationDays || DEFAULT_ORDER_EXPIRATION_DAYS,
  };
}
RetailerPermissions.propTypes = {
  changeDropshipAllowed: PropTypes.func.isRequired,
  changeReplenishAllowed: PropTypes.func.isRequired,
  approved_for_ecommerce: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  approved_for_replenishment: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  changeDropshipRevenue: PropTypes.func.isRequired,
  ecommerce_unit_price_formula: PropTypes.string,
  putPermissions: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  ecommerce_billing_method: PropTypes.string.isRequired,
  replenish_billing_method: PropTypes.string.isRequired,
  changed: PropTypes.bool.isRequired,
  minimum_products_for_replenishment_order: PropTypes.number,
  minimum_order_value_for_replenishment_order: PropTypes.number,
  replenishment_shipper_account_type: PropTypes.string,
  replenishment_shipper_account: PropTypes.string,
  setPermissionsMinimumOrderProducts: PropTypes.func.isRequired,
  setPermissionsMinimumOrderValue: PropTypes.func.isRequired,
  support_multi_currency: PropTypes.bool,
  replenishment_order_shipping_cost_percentage: PropTypes.number,
  replenishment_order_shipping_cost_fixed_per_unit: PropTypes.number,
  setPermissionsFixedPerUnit: PropTypes.func.isRequired,
  setPermissionsPercentage: PropTypes.func.isRequired,
  setPermissionsChanged: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    ecommerce_days_until_overdue_email: PropTypes.number,
    replenish_days_until_overdue_email: PropTypes.number,
  }),
};
